import { useEffect, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { catalogDefs } from "../local/db";

// card #1662 — the global test catalog (~148 rows, GET /catalog, rarely
// changes) is cached in IndexedDB (local/db.js). Hydration policy per
// docs/score/catalog-audit-findings.md section 9a: fetch once if the store
// is empty, otherwise trust the cache -- no per-show/per-scribe scoping, no
// online revalidation on every mount (unlike scribe-fe's judges/classes).
//
// `hydratePromise` is a MODULE-level (not React-state) in-flight guard: two
// callers mounting at the same moment (e.g. AdminShell's app-init call and a
// page reading the hook for its own render) dedupe onto the same fetch
// instead of both racing a GET /catalog call that would try to seed the same
// rows twice.
let hydratePromise = null;

function hydrateOnce(api) {
  if (!hydratePromise) {
    hydratePromise = (async () => {
      const cached = await catalogDefs.getAll();
      if (cached.length) return cached;
      // #1662 round 3 — no sentinel: a GET /catalog response that comes back
      // genuinely empty simply leaves the store empty (bulkPut no-ops on
      // `[]`, local/db.js), so the NEXT app-init re-fetches too rather than
      // being permanently pinned to an empty cache by a stored "already
      // hydrated" flag. See local/db.js's catalogDefs comment for why that
      // tradeoff is fine here (a cheap, idempotent, self-correcting global
      // GET, not a per-show/per-scribe fetch).
      const fetched = unwrap(await api.catalog.listCatalog());
      await catalogDefs.putAll(fetched);
      return fetched;
    })().catch((error) => {
      // Allow a later call (e.g. a subsequent page visit) to retry rather
      // than pinning the whole session to one failed fetch.
      hydratePromise = null;
      throw error;
    });
  }
  return hydratePromise;
}

// #2935 — Cypress-to-RTL migration. Test-only: clears the module-level
// in-flight/settled hydration guard so the NEXT render's useCatalogDefs call
// does a genuine fetch-if-empty pass again, instead of reusing whatever a
// PRIOR test's mount already resolved. Pairs with local/db.js's own
// `_resetDbForTests` (which empties the IndexedDB store this hook reads) —
// call both, DB first, so a fresh hydration actually sees an empty cache.
export function _resetCatalogHydrationForTests() {
  hydratePromise = null;
}

export function useCatalogDefs() {
  const api = useScoreApi();
  const [state, setState] = useState({ catalogDefs: [], loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    hydrateOnce(api)
      .then((rows) => {
        if (!cancelled) setState({ catalogDefs: rows, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ catalogDefs: [], loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  return state;
}
